import React, { useEffect, useState } from 'react';
import FeedCard from './cards/FeedCard';

const DEFAULT_API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// What each trend type counts, so a card can show the real numbers.
const TREND_UNIT = {
  search_spike: 'searches and clicks',
  watchlist_growth: 'watchlist adds',
  release_interest: 'title views',
  follow_growth: 'follows',
};

// A comma-separated event_type list matches any of them (backend: any number
// of event types in one call, see insider_routes.py /events).
const eventsUrl = (types, { daysBack = 14, daysAhead = 0, limit = 12 } = {}) =>
  `/api/insider/events?event_type=${types.join(',')}&days_back=${daysBack}&days_ahead=${daysAhead}&limit=${limit}`;

// docs/MVP.md P5 asks for 7 homepage sections. Only 3 are net-new here:
// - Releases Today / Streaming This Week / New Trailers are already covered
//   by UpcomingByDate.js (day-bucketed poster rows off the same calendar
//   endpoint) — building them again here would duplicate that section.
// - Festival News has no backing event type or data source at all yet
//   (checked backend/insider/extraction.py's EVENT_TYPES) — deferred.
// That leaves Production Updates, Casting News and Trending Searches as the
// only sections with real, unshown-elsewhere content.
function buildSections() {
  return [
    {
      id: 'production-updates',
      title: 'Production updates',
      endpoint: eventsUrl([
        'FILMING_STARTED', 'FILMING_WRAPPED', 'POST_PRODUCTION_STARTED',
        'PRODUCTION_DELAYED', 'PROJECT_GREENLIT', 'PROJECT_ENTERED_PRE_PRODUCTION',
      ]),
      mapItems: (json) =>
        (json.events || []).map((e) => ({
          type: 'event', date: e.event_date, title: e.title, media_type: e.media_type,
          poster_url: e.poster_url, tmdb_id: e.tmdb_id, project_id: e.project_id, event_type: e.event_type,
        })),
    },
    {
      id: 'casting-news',
      title: 'Casting news',
      endpoint: eventsUrl(['CAST_ATTACHED', 'DIRECTOR_ATTACHED', 'WRITER_ATTACHED']),
      mapItems: (json) =>
        (json.events || []).map((e) => ({
          type: 'event', date: e.event_date, title: e.title, media_type: e.media_type,
          poster_url: e.poster_url, tmdb_id: e.tmdb_id, project_id: e.project_id, event_type: e.event_type,
        })),
    },
    {
      id: 'trending-searches',
      title: 'Trending searches',
      endpoint: '/api/trends?trend_type=search_spike&limit=12',
      mapItems: (json) =>
        (json.trends || []).map((t) => ({
          type: 'trend', entity: t.title, entity_id: t.entity_id, project_id: t.entity_id,
          tmdb_id: t.tmdb_id, media_type: t.media_type, poster_url: t.poster_url,
          trend_type: t.trend_type, percentage_change: t.percentage_change,
          reason: typeof t.metric_value === 'number'
            ? `${t.metric_value} ${TREND_UNIT[t.trend_type] || 'events'} in ${t.time_window || ''}, ${t.baseline_value ?? 0} before`
            : `${(t.trend_type || '').replace(/_/g, ' ')} up ${Math.round(t.percentage_change || 0)}% (${t.time_window || ''})`,
        })),
    },
  ];
}

// One section: a heading and a short horizontal row of FeedCards. Renders
// nothing once loaded with no results — a quiet day shouldn't leave an
// empty, labelled box on the homepage.
function FeedSection({ apiBase, section, onResolved }) {
  const [state, setState] = useState({ items: [], loading: true });

  useEffect(() => {
    let cancelled = false;
    fetch(`${apiBase}${section.endpoint}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        const items = json ? section.mapItems(json) : [];
        if (!cancelled) { setState({ items, loading: false }); onResolved(items.length > 0); }
      })
      .catch(() => { if (!cancelled) { setState({ items: [], loading: false }); onResolved(false); } });
    return () => { cancelled = true; };
  }, [apiBase, section]);

  if (!state.loading && state.items.length === 0) return null;

  return (
    <div className="mb-6 last:mb-0">
      <h3 className="text-sm font-semibold text-gray-900 mb-2">{section.title}</h3>
      {state.loading ? (
        <div className="flex gap-3 overflow-hidden">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex-shrink-0 w-64 h-[72px] rounded-xl bg-gray-100 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {state.items.map((item, i) => (
            <div key={i} className="flex-shrink-0 w-64">
              <FeedCard r={item} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// The homepage "behind the scenes" block (docs/MVP.md P5, the slice not
// already covered by UpcomingByDate): production/casting news and trending
// searches. Read-only: no follow/watchlist affordance here yet (P4 follow-up).
export default function IntelligenceFeed({ apiBase = DEFAULT_API }) {
  const [sections] = useState(buildSections);
  // id -> hasItems, set once each section's fetch resolves. Once every
  // section has resolved and none had anything, hide the whole block rather
  // than showing an empty heading on a quiet day.
  const [resolved, setResolved] = useState({});
  const allResolved = Object.keys(resolved).length === sections.length;
  const anyVisible = Object.values(resolved).some(Boolean);
  if (allResolved && !anyVisible) return null;

  return (
    <div className="bg-gray-50 py-8 px-4 border-b border-gray-100">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Behind the scenes</h2>
        {sections.map((section) => (
          <FeedSection
            key={section.id}
            apiBase={apiBase}
            section={section}
            onResolved={(hasItems) => setResolved((s) => ({ ...s, [section.id]: hasItems }))}
          />
        ))}
      </div>
    </div>
  );
}
